import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  CalendarClock,
  Globe,
  Lock,
  TrendingDown,
  TrendingUp,
  UserCheck,
  Users,
  Wallet,
} from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Input';
import { EmptyState, PageLoader } from '@/components/ui/States';
import { formatCompact, formatDate, formatIdr, formatNumber } from '@/lib/format';
import { useThemeStore } from '@/store/theme';
import { cn } from '@/lib/cn';

/**
 * Laporan dan statistik platform.
 *
 * Angka uang hanya tampil untuk root, mengikuti batas yang sudah berlaku di
 * halaman Transaksi. Admin tetap melihat pertumbuhan akun dan keandalan
 * eksekusi, karena itu yang ia perlukan untuk menjawab pertanyaan pelanggan.
 *
 * Tiap angka diberi pembanding, bukan berdiri sendiri. "1.240 eksekusi"
 * tidak memberi tahu apa-apa; "1.240 eksekusi, 3 persen gagal" memberi tahu
 * apakah ada yang perlu dikerjakan hari ini.
 */

const RENTANG = [
  [6, '6 bulan terakhir'],
  [12, '12 bulan terakhir'],
  [24, '24 bulan terakhir'],
];

const warnaSumbu = (gelap) => ({
  grid: gelap ? '#262d38' : '#e3e6eb',
  axis: gelap ? '#7c8697' : '#6b7683',
  kursor: gelap ? '#ffffff0a' : '#0000000a',
});

/** Satu angka utama beserta keterangan pembandingnya. */
const Kartu = ({ icon: Icon, label, nilai, catatan, nada, tren }) => (
  <Card>
    <CardBody className="space-y-1">
      <p className="flex items-center gap-2 text-xs font-semibold text-[var(--text-muted)]">
        <Icon className="size-3.5" aria-hidden="true" />
        {label}
      </p>
      <p className={cn('text-2xl font-bold', nada ?? 'text-[var(--text-primary)]')}>{nilai}</p>
      {catatan && (
        <p className="flex items-center gap-1 text-xs text-[var(--text-secondary)]">
          {tren === 'naik' && <TrendingUp className="size-3.5 text-success-600" aria-hidden="true" />}
          {tren === 'turun' && (
            <TrendingDown className="size-3.5 text-danger-600" aria-hidden="true" />
          )}
          {catatan}
        </p>
      )}
    </CardBody>
  </Card>
);

const Petunjuk = ({ active, payload, label, format = formatNumber }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-overlay)] px-3 py-2 shadow-lg">
      <p className="text-xs font-bold text-[var(--text-primary)]">{label}</p>
      <dl className="mt-1.5 space-y-0.5 text-xs">
        {payload.map((p) => (
          <div key={p.dataKey} className="flex items-center justify-between gap-4">
            <dt className="text-[var(--text-muted)]">{p.name}</dt>
            <dd className="font-semibold" style={{ color: p.color }}>
              {format(p.value)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

/** Tabel ringkas dua sampai empat kolom, dipakai daftar-daftar pendek. */
const Daftar = ({ kolom, baris, kosong, render }) => {
  if (!baris?.length) {
    return <p className="py-6 text-center text-xs text-[var(--text-muted)]">{kosong}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--border-subtle)]">
            {kolom.map((k, i) => (
              <th
                key={k}
                className={cn(
                  'pb-2 text-xs font-semibold text-[var(--text-muted)]',
                  i === 0 ? 'text-left' : 'text-right',
                )}
              >
                {k}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--border-subtle)]">{baris.map(render)}</tbody>
      </table>
    </div>
  );
};

const Sel = ({ children, kanan, tebal }) => (
  <td
    className={cn(
      'py-2 text-sm',
      kanan ? 'text-right' : 'text-left',
      tebal ? 'font-semibold text-[var(--text-primary)]' : 'text-[var(--text-secondary)]',
    )}
  >
    {children}
  </td>
);

// -- Halaman --------------------------------------------------------------

const ReportsAdminPage = () => {
  const [bulan, setBulan] = useState(12);
  const gelap = useThemeStore((s) => s.isDark);
  const w = warnaSumbu(gelap);

  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'reports', bulan],
    queryFn: () => api.get(`/platform/reports?bulan=${bulan}`),
    placeholderData: (sebelumnya) => sebelumnya,
  });

  if (isLoading) return <PageLoader label="Menghitung laporan..." />;

  const d = data?.data;
  if (!d) return null;

  const p = d.pertumbuhan;
  const o = d.operasional;
  const k = d.keuangan;

  const selisihBulan =
    k?.bulanIni && k?.bulanLalu
      ? k.bulanIni.nominal - k.bulanLalu.nominal
      : null;

  const totalEksekusi = o.bulanan.reduce((n, b) => n + b.eksekusi, 0);
  const totalGagal = o.bulanan.reduce((n, b) => n + b.gagal, 0);
  const persenGagal = totalEksekusi > 0 ? Math.round((totalGagal / totalEksekusi) * 100) : 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">Laporan</h1>
          <p className="mt-1 max-w-3xl text-sm text-[var(--text-secondary)]">
            Pendapatan, pertumbuhan akun, dan keandalan eksekusi. Angkanya dihitung ulang tiap kali
            halaman ini dibuka, jadi yang Anda lihat adalah keadaan sekarang.
          </p>
        </div>
        <Select
          value={String(bulan)}
          onChange={(e) => setBulan(Number(e.target.value))}
          className="max-w-[200px]"
        >
          {RENTANG.map(([nilai, label]) => (
            <option key={nilai} value={nilai}>
              {label}
            </option>
          ))}
        </Select>
      </div>

      {/* -- Ringkasan angka utama -- */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {k && (
          <Kartu
            icon={Wallet}
            label="Pendapatan bulan ini"
            nilai={formatIdr(k.bulanIni?.nominal ?? 0)}
            tren={selisihBulan > 0 ? 'naik' : selisihBulan < 0 ? 'turun' : undefined}
            catatan={
              k.bulanLalu
                ? `Bulan lalu ${formatIdr(k.bulanLalu.nominal)}`
                : 'Belum ada bulan pembanding'
            }
          />
        )}
        <Kartu
          icon={Users}
          label="Akun berbayar aktif"
          nilai={formatNumber(p.berbayarAktif)}
          catatan={`${p.persenBerbayar}% dari ${formatNumber(p.totalOrganisasi)} ruang kerja`}
        />
        <Kartu
          icon={UserCheck}
          label="Pengguna terdaftar"
          nilai={formatNumber(p.totalPengguna)}
          catatan={`${p.persenTerverifikasi}% sudah memverifikasi email`}
        />
        <Kartu
          icon={CalendarClock}
          label="Eksekusi gagal"
          nilai={`${persenGagal}%`}
          nada={persenGagal >= 10 ? 'text-danger-600' : undefined}
          catatan={`${formatNumber(totalGagal)} dari ${formatNumber(totalEksekusi)} eksekusi`}
        />
      </div>

      {/* -- Keuangan, khusus root -- */}
      {k ? (
        <>
          <Card>
            <CardHeader
              title="Pendapatan per bulan"
              description={`Total ${formatIdr(k.total)} dari transaksi lunas, sesudah potongan ${formatIdr(k.totalPotongan)}.`}
            />
            <CardBody>
              {k.total === 0 ? (
                <EmptyState
                  compact
                  icon={Wallet}
                  title="Belum ada pembayaran lunas"
                  description="Grafik terisi begitu transaksi pertama dibayar."
                />
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={k.bulanan} margin={{ top: 8, right: 4, bottom: 0, left: -6 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={w.grid} vertical={false} />
                    <XAxis
                      dataKey="label"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: w.axis, fontSize: 12 }}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: w.axis, fontSize: 12 }}
                      tickFormatter={formatCompact}
                      width={52}
                    />
                    <Tooltip
                      cursor={{ fill: w.kursor }}
                      content={<Petunjuk format={formatIdr} />}
                    />
                    <Bar dataKey="nominal" name="Pendapatan" fill="#3252ad" radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardBody>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader
                title="Paket terlaris"
                description="Dihitung dari transaksi yang benar-benar dibayar."
              />
              <CardBody>
                <Daftar
                  kolom={['Paket', 'Terjual', 'Nilai']}
                  baris={k.perPaket}
                  kosong="Belum ada paket terjual."
                  render={(x) => (
                    <tr key={x.id}>
                      <Sel tebal>{x.nama}</Sel>
                      <Sel kanan>{formatNumber(x.jumlah)}</Sel>
                      <Sel kanan>{formatIdr(x.nominal)}</Sel>
                    </tr>
                  )}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="Add-on terlaris"
                description="Tambahan kuota yang paling sering dibeli."
              />
              <CardBody>
                <Daftar
                  kolom={['Add-on', 'Terjual', 'Nilai']}
                  baris={k.perAddOn}
                  kosong="Belum ada add-on terjual."
                  render={(x) => (
                    <tr key={x.id}>
                      <Sel tebal>{x.nama}</Sel>
                      <Sel kanan>{formatNumber(x.jumlah)}</Sel>
                      <Sel kanan>{formatIdr(x.nominal)}</Sel>
                    </tr>
                  )}
                />
              </CardBody>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader
                title="Penyelesaian pembayaran"
                description={`${k.persenLunas}% pembelian berakhir lunas. Angka ini yang turun lebih dulu kalau ada metode pembayaran yang diam-diam rusak.`}
              />
              <CardBody>
                <Daftar
                  kolom={['Status', 'Transaksi', 'Nilai']}
                  baris={Object.entries(k.status).map(([nama, v]) => ({ nama, ...v }))}
                  kosong="Belum ada transaksi pada rentang ini."
                  render={(x) => (
                    <tr key={x.nama}>
                      <Sel tebal>
                        <Badge
                          size="sm"
                          tone={
                            x.nama === 'paid'
                              ? 'success'
                              : x.nama === 'pending'
                                ? 'warning'
                                : x.nama === 'refunded'
                                  ? 'info'
                                  : 'neutral'
                          }
                        >
                          {x.nama}
                        </Badge>
                      </Sel>
                      <Sel kanan>{formatNumber(x.jumlah)}</Sel>
                      <Sel kanan>{formatIdr(x.nominal)}</Sel>
                    </tr>
                  )}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="Kode diskon terpakai"
                description="Berapa banyak potongan yang benar-benar diberikan."
              />
              <CardBody>
                <Daftar
                  kolom={['Kode', 'Dipakai', 'Potongan']}
                  baris={k.kupon}
                  kosong="Belum ada kode diskon yang dipakai."
                  render={(x) => (
                    <tr key={x.kode}>
                      <Sel tebal>
                        <span className="font-mono">{x.kode}</span>
                      </Sel>
                      <Sel kanan>{formatNumber(x.dipakai)}</Sel>
                      <Sel kanan>{formatIdr(x.potongan)}</Sel>
                    </tr>
                  )}
                />
              </CardBody>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader
                title="Akan habis dalam 7 hari"
                description="Yang masih bisa diselamatkan. Perpanjangan di Crawel manual, jadi daftar ini perlu dibaca, bukan ditunggu."
              />
              <CardBody>
                <Daftar
                  kolom={['Akun', 'Paket', 'Berakhir']}
                  baris={d.berhenti.segeraHabis}
                  kosong="Tidak ada yang akan habis minggu ini."
                  render={(x) => (
                    <tr key={x.id}>
                      <Sel tebal>
                        {x.nama}
                        {x.pemilik && (
                          <span className="block text-xs font-normal text-[var(--text-muted)]">
                            {x.pemilik}
                          </span>
                        )}
                      </Sel>
                      <Sel kanan>{x.paket}</Sel>
                      <Sel kanan>{formatDate(x.berakhir)}</Sel>
                    </tr>
                  )}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="Berhenti berlangganan"
                description="Berbayar yang lewat tanggal lebih dari tujuh hari tanpa perpanjangan. Tenggangnya sengaja, supaya yang sedang mengurus pembayaran tidak terhitung berhenti."
              />
              <CardBody>
                <Daftar
                  kolom={['Akun', 'Paket', 'Habis sejak']}
                  baris={d.berhenti.berhenti}
                  kosong="Belum ada yang berhenti pada rentang ini."
                  render={(x) => (
                    <tr key={x.id}>
                      <Sel tebal>
                        {x.nama}
                        {x.pemilik && (
                          <span className="block text-xs font-normal text-[var(--text-muted)]">
                            {x.pemilik}
                          </span>
                        )}
                      </Sel>
                      <Sel kanan>{x.paket}</Sel>
                      <Sel kanan>{formatDate(x.berakhir)}</Sel>
                    </tr>
                  )}
                />
              </CardBody>
            </Card>
          </div>
        </>
      ) : (
        <Card>
          <CardBody className="flex items-start gap-3">
            <Lock className="mt-0.5 size-4 shrink-0 text-[var(--text-muted)]" aria-hidden="true" />
            <div>
              <p className="text-sm font-semibold text-[var(--text-primary)]">
                Angka pendapatan tidak ditampilkan
              </p>
              <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
                Laporan keuangan mengikuti batas yang sama dengan halaman Transaksi: hanya pemilik
                (root) yang bisa melihat riwayat lintas waktu. Statistik pertumbuhan dan operasional
                di bawah tetap tersedia untuk Anda.
              </p>
            </div>
          </CardBody>
        </Card>
      )}

      {/* -- Pertumbuhan -- */}
      <Card>
        <CardHeader
          title="Akun baru per bulan"
          description="Pengguna dan ruang kerja yang mendaftar tiap bulannya."
        />
        <CardBody>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={p.bulanan} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={w.grid} vertical={false} />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fill: w.axis, fontSize: 12 }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fill: w.axis, fontSize: 12 }}
                allowDecimals={false}
                width={40}
              />
              <Tooltip cursor={{ stroke: w.axis }} content={<Petunjuk />} />
              <Line
                type="monotone"
                dataKey="akun"
                name="Pengguna"
                stroke="#3252ad"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="organisasi"
                name="Ruang kerja"
                stroke="#12b76a"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardBody>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="Sebaran paket"
            description="Yang kedaluwarsa masih menyandang nama paketnya sampai ada yang menyentuh akunnya, jadi keduanya dipisahkan di sini."
          />
          <CardBody>
            <Daftar
              kolom={['Paket', 'Total', 'Masih berlaku', 'Kedaluwarsa']}
              baris={p.sebaranPaket}
              kosong="Belum ada ruang kerja."
              render={(x) => (
                <tr key={x.id}>
                  <Sel tebal>{x.nama}</Sel>
                  <Sel kanan>{formatNumber(x.jumlah)}</Sel>
                  <Sel kanan>{formatNumber(x.masihBerlaku)}</Sel>
                  <Sel kanan>
                    <span className={x.kedaluwarsa > 0 ? 'text-warning-600' : undefined}>
                      {formatNumber(x.kedaluwarsa)}
                    </span>
                  </Sel>
                </tr>
              )}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Domain paling sering di-scrape"
            description={`Dari ${formatNumber(o.totalJob)} job, ${formatNumber(o.jobBerjadwal)} di antaranya berjadwal.`}
          />
          <CardBody>
            <Daftar
              kolom={['Domain', 'Job']}
              baris={o.domainTeratas}
              kosong="Belum ada job dengan domain tercatat."
              render={(x) => (
                <tr key={x.domain}>
                  <Sel tebal>
                    <span className="flex items-center gap-2">
                      <Globe className="size-3.5 text-[var(--text-muted)]" aria-hidden="true" />
                      {x.domain}
                    </span>
                  </Sel>
                  <Sel kanan>{formatNumber(x.jumlah)}</Sel>
                </tr>
              )}
            />
          </CardBody>
        </Card>
      </div>

      {/* -- Operasional -- */}
      <Card>
        <CardHeader
          title="Eksekusi per bulan"
          description="Batang merah adalah yang gagal. Kalau bagian merahnya tumbuh lebih cepat daripada seluruh batangnya, ada yang perlu dilihat."
        />
        <CardBody>
          {totalEksekusi === 0 ? (
            <EmptyState
              compact
              icon={CalendarClock}
              title="Belum ada eksekusi"
              description="Grafik terisi setelah job pertama dijalankan."
            />
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={o.bulanan} margin={{ top: 8, right: 4, bottom: 0, left: -14 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={w.grid} vertical={false} />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: w.axis, fontSize: 12 }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: w.axis, fontSize: 12 }}
                  allowDecimals={false}
                  width={44}
                />
                <Tooltip cursor={{ fill: w.kursor }} content={<Petunjuk />} />
                <Bar dataKey="eksekusi" name="Eksekusi" fill="#3252ad" radius={[5, 5, 0, 0]} />
                <Bar dataKey="gagal" name="Gagal" fill="#f04438" radius={[5, 5, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default ReportsAdminPage;
